'use client'

import { FormField, FormSection } from '@/components/form-section'
import {
    getMessagingConfig,
    updateMessagingConfig,
    type ChatAutoCloseMode,
} from '@/lib/api/messaging/messaging-config-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useState } from 'react'
import { MdCheckCircleOutline, MdSave, MdSmartToy, MdTimerOff } from 'react-icons/md'
import { toast } from 'react-toastify'

const MIN_HOURS = 1
const MAX_HOURS = 720

/** Espelha `AI_RESUME_MAX_MINUTES` do backend: 30 dias. */
const MAX_AI_RESUME_MINUTES = 43200

/** Espelha `AI_RESUME_IDLE_MAX_HOURS` do backend: 30 dias. */
const MAX_AI_RESUME_IDLE_HOURS = 720

const MODE_OPTIONS: { value: ChatAutoCloseMode; title: string; description: string }[] = [
    {
        value: 'WINDOW',
        title: 'Quando a janela de 24 horas fecha',
        description:
            'O atendimento fecha 24 horas depois da última mensagem do contato, quando a Meta deixa de aceitar texto livre. Recomendado.',
    },
    {
        value: 'HOURS',
        title: 'Depois de um tempo sem mensagem do contato',
        description: 'Você escolhe quantas horas o atendimento fica aberto depois da última mensagem do contato.',
    },
    {
        value: 'NEVER',
        title: 'Nunca fechar automaticamente',
        description: 'O atendimento só fecha quando alguém clica em Encerrar.',
    },
]

/** Mensageria > Configurações: regras da organização para os atendimentos. */
export default function MessagingSettingsPage() {
    const { canRead, canUpdate } = useResourcePermissions('messaging', 'settings')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [mode, setMode] = useState<ChatAutoCloseMode>('WINDOW')
    const [hours, setHours] = useState<string>('')
    const [aiResumeMinutes, setAiResumeMinutes] = useState<string>('')
    const [aiResumeIdleHours, setAiResumeIdleHours] = useState<string>('')
    const [saved, setSaved] = useState<{
        mode: ChatAutoCloseMode
        hours: string
        aiResumeMinutes: string
        aiResumeIdleHours: string
    } | null>(null)

    useEffect(() => {
        if (!canRead) return
        getMessagingConfig()
            .then(res => {
                if (res.success && res.result) {
                    const loadedHours = res.result.chatAutoCloseHours != null ? String(res.result.chatAutoCloseHours) : ''
                    const loadedResume =
                        res.result.aiResumeAfterMinutes != null ? String(res.result.aiResumeAfterMinutes) : ''
                    const loadedIdle =
                        res.result.aiResumeIdleHours != null ? String(res.result.aiResumeIdleHours) : ''
                    setMode(res.result.chatAutoCloseMode)
                    setHours(loadedHours)
                    setAiResumeMinutes(loadedResume)
                    setAiResumeIdleHours(loadedIdle)
                    setSaved({
                        mode: res.result.chatAutoCloseMode,
                        hours: loadedHours,
                        aiResumeMinutes: loadedResume,
                        aiResumeIdleHours: loadedIdle,
                    })
                } else {
                    toast.error(res.error || 'Não foi possível carregar as configurações')
                }
            })
            .catch(() => toast.error('Não foi possível carregar as configurações'))
            .finally(() => setLoading(false))
    }, [canRead])

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">Você não tem permissão para acessar as configurações de mensageria.</div>
            </div>
        )
    }

    const hoursNumber = Number(hours)
    const hoursInvalid = mode === 'HOURS' && (!Number.isInteger(hoursNumber) || hoursNumber < MIN_HOURS || hoursNumber > MAX_HOURS)

    // Campo vazio é uma escolha válida ("não volta sozinha"), e por isso não é erro.
    const aiResumeNumber = Number(aiResumeMinutes)
    const aiResumeInvalid =
        aiResumeMinutes.trim() !== '' &&
        (!Number.isInteger(aiResumeNumber) || aiResumeNumber < 0 || aiResumeNumber > MAX_AI_RESUME_MINUTES)

    const aiResumeIdleNumber = Number(aiResumeIdleHours)
    const aiResumeIdleInvalid =
        aiResumeIdleHours.trim() !== '' &&
        (!Number.isInteger(aiResumeIdleNumber) ||
            aiResumeIdleNumber < 0 ||
            aiResumeIdleNumber > MAX_AI_RESUME_IDLE_HOURS)
    const aiResumeEnabled = aiResumeMinutes.trim() !== '' && aiResumeNumber > 0

    const dirty =
        !!saved &&
        (saved.mode !== mode ||
            (mode === 'HOURS' && saved.hours !== hours) ||
            saved.aiResumeMinutes !== aiResumeMinutes ||
            saved.aiResumeIdleHours !== aiResumeIdleHours)

    const handleSave = async () => {
        if (hoursInvalid || aiResumeInvalid || aiResumeIdleInvalid || !canUpdate) return
        setSaving(true)
        try {
            const res = await updateMessagingConfig({
                chatAutoCloseMode: mode,
                chatAutoCloseHours: mode === 'HOURS' ? hoursNumber : null,
                aiResumeAfterMinutes: aiResumeMinutes.trim() === '' ? null : aiResumeNumber,
                aiResumeIdleHours: aiResumeIdleHours.trim() === '' ? null : aiResumeIdleNumber,
            })
            if (res.success && res.result) {
                const savedHours = res.result.chatAutoCloseHours != null ? String(res.result.chatAutoCloseHours) : ''
                const savedResume =
                    res.result.aiResumeAfterMinutes != null ? String(res.result.aiResumeAfterMinutes) : ''
                const savedIdle =
                    res.result.aiResumeIdleHours != null ? String(res.result.aiResumeIdleHours) : ''
                setHours(savedHours)
                setAiResumeMinutes(savedResume)
                setAiResumeIdleHours(savedIdle)
                setSaved({
                    mode: res.result.chatAutoCloseMode,
                    hours: savedHours,
                    aiResumeMinutes: savedResume,
                    aiResumeIdleHours: savedIdle,
                })
                toast.success('Configurações salvas')
            } else {
                toast.error(res.error || 'Não foi possível salvar as configurações')
            }
        } catch {
            toast.error('Não foi possível salvar as configurações')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
            <div>
                <h1 className="text-2xl font-bold">Configurações de mensageria</h1>
                <p className="text-sm text-base-content/70">Regras que valem para todos os atendimentos da organização.</p>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            ) : (
                <FormSection
                    icon={<MdTimerOff className="text-lg" />}
                    title="Encerramento automático de atendimentos"
                    subtitle="Quando um atendimento aberto fecha sem ninguém clicar em Encerrar."
                >
                    <fieldset className="flex flex-col gap-3" disabled={!canUpdate || saving}>
                        <legend className="sr-only">Quando fechar</legend>
                        {MODE_OPTIONS.map(option => {
                            const selected = mode === option.value
                            return (
                                <label
                                    key={option.value}
                                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                                        selected ? 'border-secondary bg-secondary/5' : 'border-base-300 hover:border-base-content/30'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="chat-auto-close-mode"
                                        className="radio radio-secondary radio-sm mt-0.5"
                                        checked={selected}
                                        onChange={() => setMode(option.value)}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-base-content">{option.title}</span>
                                        <span className="mt-1 block text-xs leading-relaxed text-base-content/60">{option.description}</span>

                                        {option.value === 'HOURS' && selected && (
                                            <span className="mt-4 block max-w-xs">
                                                <FormField
                                                    label="Horas sem mensagem do contato"
                                                    htmlFor="chat-auto-close-hours"
                                                    required
                                                    hint={hoursInvalid ? undefined : `De ${MIN_HOURS} a ${MAX_HOURS} horas (30 dias).`}
                                                >
                                                    <input
                                                        id="chat-auto-close-hours"
                                                        type="number"
                                                        inputMode="numeric"
                                                        min={MIN_HOURS}
                                                        max={MAX_HOURS}
                                                        step={1}
                                                        className={`input input-bordered input-sm w-full ${hoursInvalid ? 'input-error' : ''}`}
                                                        value={hours}
                                                        onChange={e => setHours(e.target.value)}
                                                    />
                                                    {hoursInvalid && (
                                                        <span className="text-xs text-error">
                                                            Informe um número inteiro de {MIN_HOURS} a {MAX_HOURS}.
                                                        </span>
                                                    )}
                                                </FormField>
                                            </span>
                                        )}
                                    </span>
                                </label>
                            )
                        })}
                    </fieldset>

                    <div className="rounded-xl bg-base-200/50 p-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/50">Vale em qualquer opção</p>
                        <ul className="flex flex-col gap-2 text-xs leading-relaxed text-base-content/70">
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                Atendimento em que o contato espera resposta nunca fecha sozinho: fica na fila até alguém responder.
                            </li>
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                Mensagem enviada por fluxo automático não conta como atividade do atendimento.
                            </li>
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                Se o contato escrever depois, o mesmo atendimento reabre.
                            </li>
                        </ul>
                    </div>

                </FormSection>
            )}

            {!loading && (
                <FormSection
                    icon={<MdSmartToy className="text-lg" />}
                    title="Retomada da IA depois de um atendimento humano"
                    subtitle="Quando alguém da equipe responde, a IA para de responder naquela conversa. Aqui você decide quando ela volta."
                >
                    <div className="grid gap-5 sm:grid-cols-2">
                        <FormField
                            label="Minutos que o contato espera sem resposta"
                            htmlFor="ai-resume-minutes"
                            hint={
                                aiResumeInvalid
                                    ? undefined
                                    : `Passado esse tempo sem ninguém responder, a IA entra. Vazio ou 0: a IA não volta sozinha.`
                            }
                        >
                            <input
                                id="ai-resume-minutes"
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={MAX_AI_RESUME_MINUTES}
                                step={1}
                                placeholder="Não volta sozinha"
                                disabled={!canUpdate || saving}
                                className={`input input-bordered input-sm w-full ${aiResumeInvalid ? 'input-error' : ''}`}
                                value={aiResumeMinutes}
                                onChange={e => setAiResumeMinutes(e.target.value)}
                            />
                            {aiResumeInvalid && (
                                <span className="text-xs text-error">
                                    Informe um número inteiro de 0 a {MAX_AI_RESUME_MINUTES}.
                                </span>
                            )}
                        </FormField>

                        <FormField
                            label="Horas para a conversa contar como parada"
                            htmlFor="ai-resume-idle-hours"
                            hint={
                                aiResumeIdleInvalid
                                    ? undefined
                                    : 'Sem mensagem de ninguém por esse tempo, quem escrever é atendido pela IA na hora. Vazio ou 0: sempre espera o prazo ao lado.'
                            }
                        >
                            <input
                                id="ai-resume-idle-hours"
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={MAX_AI_RESUME_IDLE_HOURS}
                                step={1}
                                placeholder="Sem regra"
                                disabled={!canUpdate || saving || !aiResumeEnabled}
                                className={`input input-bordered input-sm w-full ${aiResumeIdleInvalid ? 'input-error' : ''}`}
                                value={aiResumeIdleHours}
                                onChange={e => setAiResumeIdleHours(e.target.value)}
                            />
                            {aiResumeIdleInvalid && (
                                <span className="text-xs text-error">
                                    Informe um número inteiro de 0 a {MAX_AI_RESUME_IDLE_HOURS}.
                                </span>
                            )}
                        </FormField>
                    </div>

                    <div className="rounded-xl bg-base-200/50 p-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/50">
                            Como funciona
                        </p>
                        <ul className="flex flex-col gap-2 text-xs leading-relaxed text-base-content/70">
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                A IA nunca responde no instante em que o contato escreve numa conversa da
                                equipe: ela espera o prazo, e só entra se ninguém tiver respondido.
                            </li>
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                Como ela entra depende do setor (Departamentos): de retaguarda, só segura o
                                contato e deixa a conversa com a equipe; ou assumindo a conversa, em setores
                                atendidos só pela IA.
                            </li>
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                "Ok" e "obrigada" não pedem resposta, e a IA não entra por eles.
                            </li>
                            <li className="flex gap-2">
                                <MdCheckCircleOutline className="mt-0.5 shrink-0 text-success" />
                                Atendimento que está aguardando uma pessoa continua sem a IA, por mais tempo
                                que passe.
                            </li>
                        </ul>
                    </div>
                </FormSection>
            )}

            {!loading && canUpdate && (
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-2"
                        onClick={handleSave}
                        disabled={!dirty || hoursInvalid || aiResumeInvalid || aiResumeIdleInvalid || saving}
                    >
                        {saving ? <span className="loading loading-spinner loading-xs" /> : <MdSave className="text-base" />}
                        Salvar
                    </button>
                </div>
            )}
        </div>
    )
}
